import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import PublicLayoutAppBar from "./components/PublicLayoutAppbar";
import { styled, useTheme, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SetupDialog from "src/pages/dashboard/SetupDialog";
import SubscriptionPlanDialog from "src/@core/components/SubscriptionPlanDialog";
import PaymentSuccessDialog from "src/@core/components/PaymentSuccessDialog";
import PaymentErrorDialog from "src/@core/components/PaymentErrorDialog";
import BusinessDeactivationDialog from "src/@core/components/BusinessDeactivationDialog";
import DownloadAppBox from "src/components/common/DownloadApp";
import { APP_URLS } from "src/hooks/useAppDetection";
import { setIsDownloadBoxVisible } from "src/redux/slices/AppSlice";

// Constants
const SIDEBAR_WIDTH_EXPANDED = 350;
const SIDEBAR_WIDTH_COLLAPSED = 0;

// Routes that should use Dialog instead of Sidebar
const DIALOG_ROUTES = ["/dashboard", "/chats"];

const PublicLayoutWrapper = styled(Box)(({ theme }) => ({
  height: "100vh",
  overflow: "hidden",
  margin: 0,
  padding: 0,
}));

const DOWNLOAD_BOX_HEIGHT = 40;

const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "toolbarHeight" && prop !== "downloadBoxOffset",
})(({ theme, sidebarExpanded, useDialog, toolbarHeight, downloadBoxOffset }) => ({
  position: "absolute",
  top: `${toolbarHeight + (downloadBoxOffset || 0)}px`,
  left: useDialog
    ? 0
    : sidebarExpanded
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_COLLAPSED,
  transition: useDialog ? "none" : "left 0.3s ease-in-out",
  width: useDialog
    ? "100%"
    : sidebarExpanded
    ? `calc(100% - ${SIDEBAR_WIDTH_EXPANDED}px)`
    : "100%",
  height: `calc(100vh - ${toolbarHeight + (downloadBoxOffset || 0)}px)`,
  overflowY: "auto",
  overflowX: "hidden",
  backgroundColor: "#FFFFFF",
}));

const PublicLayout = (props) => {
  const { children } = props;
  const theme = useTheme();
  const location = useLocation();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const isDownloadBoxVisible = useSelector((state) => state.app.isDownloadBoxVisible);
  const isAppInstalled = useSelector((state) => state.app.isAppInstalled);

  // Show download box on fresh load/refresh, not on in-app navigation
  useEffect(() => {
    const isPageRefresh = !sessionStorage.getItem("app_initialized");
    if (isPageRefresh) {
      dispatch(setIsDownloadBoxVisible(true));
      sessionStorage.setItem("app_initialized", "true");
    }
    const handleBeforeUnload = () => sessionStorage.removeItem("app_initialized");
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dispatch]);

  // Determine if current route should use dialog
  const shouldUseDialog = DIALOG_ROUTES.includes(location.pathname);

  // Get the toolbar height from theme
  const toolbarHeight = theme.mixins.toolbar.minHeight || 56;

  // Download box offset when visible (use isMobileViewport to match banner render signal)
  const downloadBoxOffset =
    isMobileViewport && isDownloadBoxVisible && isAppInstalled !== null
      ? DOWNLOAD_BOX_HEIGHT
      : 0;

  const handleOpenApp = () => {
    window.location.href = APP_URLS.deepLink;
  };

  const handleDownloadApp = () => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    window.location.href = isAndroid ? APP_URLS.playStore : APP_URLS.appStore;
  };

  const handleHideBox = () => {
    dispatch(setIsDownloadBoxVisible(false));
  };

  const handleSidebarToggle = (isExpanded) => {
    // Only update sidebar state if not using dialog
    if (!shouldUseDialog) {
      setSidebarExpanded(isExpanded);
    }
  };

  return (
    <PublicLayoutWrapper>
      {isMobileViewport && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.drawer + 2,
            bgcolor: "#6e3fc6",
            overflow: "hidden",
          }}
        >
          <DownloadAppBox
            isDownloadBoxVisible={isDownloadBoxVisible}
            isMobile={isMobileViewport}
            isAppInstalled={isAppInstalled}
            handleOpenApp={handleOpenApp}
            handleDownloadApp={handleDownloadApp}
            handleHideBox={handleHideBox}
          />
        </Box>
      )}
      <PublicLayoutAppBar
        onSidebarToggle={handleSidebarToggle}
        useSidebarDialog={shouldUseDialog}
        topOffset={downloadBoxOffset}
      />
      <SubscriptionPlanDialog />
      <BusinessDeactivationDialog />
      <SetupDialog />
      <PaymentSuccessDialog />
      <PaymentErrorDialog />

      <ContentWrapper
        id="publicLayoutScroll"
        sidebarExpanded={sidebarExpanded}
        useDialog={shouldUseDialog}
        toolbarHeight={toolbarHeight}
        downloadBoxOffset={downloadBoxOffset}
        style={{
          ["--app-header-height"]: `${toolbarHeight + (downloadBoxOffset || 0)}px`,
        }}
      >
        {children}
      </ContentWrapper>
    </PublicLayoutWrapper>
  );
};

export default PublicLayout;
